import React from 'react';
import NotificationsIcon from '@mui/icons-material/Notifications';
import Box from '@mui/material/Box';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Tooltip from '@mui/material/Tooltip';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { getUserNotifications, readUserNotifications } from '../../utils/api';
import { useNavigate } from 'react-router-dom';
const moment = require('moment');

const NotificationDropDown = ({ userId }) => {
  const [anchorEl, setAnchorEl] = React.useState(null);
  const open = Boolean(anchorEl);
  const queryClient = useQueryClient();
  const navigate = useNavigate();

  const { data } = useQuery(['getNotifications', userId], () => getUserNotifications(userId), {
    enabled: !!userId,
  });
  const notifications = data?.data || [];

  // Create mutation for marking a notification as read
  const markAsReadMutation = useMutation((notificationId) => readUserNotifications(userId, notificationId), {
    onSuccess: () => {
      // Invalidate and refetch notifications after marking as read
      queryClient.invalidateQueries(['getNotifications', userId]);
    },
  });

  // Create mutation for marking all notifications as read
  const markAllAsReadMutation = useMutation(() => readUserNotifications(userId), {
    onSuccess: () => {
      // Invalidate and refetch notifications after marking all as read
      queryClient.invalidateQueries(['getNotifications', userId]);
      // Close the menu after action
      handleClose();
    },
  });

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleMarkAllAsRead = () => {
    // Use mutation to mark all notifications as read
    markAllAsReadMutation.mutate();
  };

  const handleReadNotification = (notificationId, notification) => {
    if (!notification.isRead) {
      markAsReadMutation.mutate(notificationId);
    }
    // Handle navigation based on notification type
    if (notification.metadata) {
      const { type } = notification.metadata;

      switch (type) {
        case 'CAREGIVER_INVITATION':
          navigate('/caregivers');
          break;
        case 'CARE_RECIPIENT_INVITATION':
          navigate('/care-recipients');
          break;
        case 'MEDICATION_ASSIGNED':
          navigate('/medications');
          break;
        default:
          // For unknown types, just close the menu
          handleClose();
      }
    }

    handleClose();
  };

  return (
    <React.Fragment>
      <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
        <Tooltip title="Notifications">
          <div style={{ display: 'flex', cursor: 'pointer', marginInline: '20px' }} onClick={handleClick}>
            <NotificationsIcon
              style={{
                fontSize: '20px',
                padding: '5px',
                borderRadius: '50%',
                background: '#fff',
              }}
            />
            {notifications.some((notification) => !notification.isRead) && (
              <div
                style={{
                  backgroundColor: 'red',
                  height: '8px',
                  width: '8px',
                  borderRadius: '50%',
                  marginLeft: '-8px',
                  marginTop: '2px',
                }}
              />
            )}
          </div>
        </Tooltip>
      </Box>

      <Menu
        anchorEl={anchorEl}
        id="notifications-menu"
        open={open}
        onClose={handleClose}
        slotProps={{
          paper: {
            elevation: 0,
            sx: {
              '&::before': {
                content: '""',
                display: 'block',
                position: 'absolute',
                top: 0,
                right: 14,
                width: 10,
                height: 10,
                bgcolor: 'background.paper',
                transform: 'translateY(-50%) rotate(45deg)',
                zIndex: 0,
              },
            },
          },
        }}
        PaperProps={{
          style: {
            width: '350px',
            maxWidth: '350px',
          },
        }}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
        anchorPosition={{ top: 64, left: window.innerWidth }}
      >
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            padding: '12px 16px',
            borderBottom: '1px solid #eee',
          }}
        >
          <Typography variant="subtitle1" fontWeight="bold">
            All notifications
          </Typography>
          <Button
            size="small"
            sx={{ textTransform: 'none', color: '#01796b' }}
            onClick={handleMarkAllAsRead}
            disabled={markAllAsReadMutation.isLoading}
          >
            {markAllAsReadMutation.isLoading ? 'Marking...' : 'Mark all as read'}
          </Button>
        </Box>

        {notifications.length > 0 ? (
          notifications.map((notification) => (
            <MenuItem
              key={notification._id}
              sx={{
                padding: '12px 16px',
                whiteSpace: 'normal',
                borderBottom: '1px solid #f5f5f5',
              }}
            >
              <Box sx={{ width: '100%' }}>
                <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                  <Box sx={{ position: 'relative', mr: 2 }}>
                    <div
                      style={{
                        backgroundColor: notification.isRead ? 'transparent' : '#f44336',
                        width: '10px',
                        height: '10px',
                        borderRadius: '50%',
                        marginTop: '5px',
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }} onClick={() => handleReadNotification(notification._id, notification)}>
                    <Typography variant="body2">{notification.message}</Typography>
                    <Typography variant="caption" color="text.secondary">
                      {notification.timestamp || moment.unix(notification.createdAt).fromNow()}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </MenuItem>
          ))
        ) : (
          <Box sx={{ p: 2, textAlign: 'center' }}>
            <Typography variant="body2" color="text.secondary">
              No notifications
            </Typography>
          </Box>
        )}
      </Menu>
    </React.Fragment>
  );
};

export default NotificationDropDown;
